// @flow
import ZentakeFormContext from 'components/ZentakeForm/ZentakeFormContext'
import orderBy from 'lodash/orderBy'
import * as React from 'react'
import type { Page } from 'types/page'
import type { Response } from 'types/response'
import type { Section } from 'types/section'
import type { SessionObject } from 'types/session-object'
import { hasText, isFilledArray } from 'utils/helpers'

import cn from '@/utils/tailwind/cn'

import FormSectionQuestionRow from './FormSectionQuestionRow'

type Props = {
  section: Section,
  response: Response,
  sessions: SessionObject,
  className?: string,
  isRepeatableSection?: boolean,
  page: Page,
}

function FormSection({
  section,
  response,
  sessions,
  className,
  isRepeatableSection = false,
  page,
}: Props): React.Node {
  const {
    getAnswer,
    getQuestionError,
    isInternalForm,
    isQuestionDirty,
    isQuestionReadonly,
    isQuestionValid,
    mode,
    onAnswerChanged,
    questionIdToScroll,
    responseView,
    setQuestionIdToScroll,
    setResponse,
    visibleByConditionalRules,
    willShowNotAllowedCursor,
  } = React.useContext(ZentakeFormContext)

  const filterQuestionsForRepeatableSection = React.useCallback(
    (questions) => {
      if (!isRepeatableSection) return questions

      return questions.filter((question) => {
        const questionType = question.question_type?.question_type
        return !['matrix', 'table'].includes(questionType)
      })
    },
    [isRepeatableSection]
  )

  const renderDeprecatedRows = React.useCallback(() => {
    const filteredQuestions = filterQuestionsForRepeatableSection(
      orderBy(section.questions, ['order'], ['asc'])
    )

    return filteredQuestions.map((question) => (
      <FormSectionQuestionRow
        answer={getAnswer(question)}
        isInternalForm={isInternalForm}
        isQuestionDirty={isQuestionDirty(question)}
        isQuestionReadonly={isQuestionReadonly(question)}
        isQuestionValid={isQuestionValid(question)}
        key={question.id}
        mode={mode}
        onAnswerChanged={onAnswerChanged}
        page={page}
        question={question}
        questionError={getQuestionError(question)}
        questionIdToScroll={questionIdToScroll}
        response={response}
        responseView={responseView}
        section={section}
        sessions={sessions}
        setQuestionIdToScroll={setQuestionIdToScroll}
        setResponse={setResponse}
        visibleByConditionalRules={visibleByConditionalRules(question)}
        willShowNotAllowedCursor={willShowNotAllowedCursor(question)}
      />
    ))
  }, [
    filterQuestionsForRepeatableSection,
    section,
    page,
    getAnswer,
    isInternalForm,
    isQuestionDirty,
    isQuestionReadonly,
    isQuestionValid,
    mode,
    onAnswerChanged,
    getQuestionError,
    questionIdToScroll,
    response,
    responseView,
    sessions,
    setQuestionIdToScroll,
    setResponse,
    visibleByConditionalRules,
    willShowNotAllowedCursor,
  ])

  const renderRows = React.useCallback(() => {
    const rows = orderBy(section.rows, ['order'], ['asc'])

    return rows.map((row, index) => {
      const allQuestions = orderBy(row.questions, ['order'], ['asc'])
      const questions = filterQuestionsForRepeatableSection(allQuestions)

      let totalDisplaySize = 0
      const MAX_DISPLAY_SIZE = 12

      let isVisibleByConditionalRules = true
      let isHidden = false

      return (
        <React.Fragment key={index}>
          {questions.map((question) => {
            totalDisplaySize += question.display_columns

            const getVisibleByConditionalRules =
              visibleByConditionalRules(question)
            if (!responseView) {
              isVisibleByConditionalRules = getVisibleByConditionalRules
            }
            if (!responseView) isHidden = question?.hidden

            return (
              <FormSectionQuestionRow
                answer={getAnswer(question)}
                isInternalForm={isInternalForm}
                isQuestionDirty={isQuestionDirty(question)}
                isQuestionReadonly={isQuestionReadonly(question)}
                isQuestionValid={isQuestionValid(question)}
                key={question.id}
                mode={mode}
                onAnswerChanged={onAnswerChanged}
                page={page}
                question={question}
                questionError={getQuestionError(question)}
                questionIdToScroll={questionIdToScroll}
                response={response}
                responseView={responseView}
                section={section}
                sessions={sessions}
                setQuestionIdToScroll={setQuestionIdToScroll}
                setResponse={setResponse}
                visibleByConditionalRules={getVisibleByConditionalRules}
                willShowNotAllowedCursor={willShowNotAllowedCursor(question)}
              />
            )
          })}

          {totalDisplaySize < MAX_DISPLAY_SIZE &&
          !(!isVisibleByConditionalRules && !isHidden) ? (
            <div
              style={{
                gridColumn: `span ${(
                  MAX_DISPLAY_SIZE - totalDisplaySize
                ).toString()}`,
              }}
            />
          ) : null}
        </React.Fragment>
      )
    })
  }, [
    section,
    filterQuestionsForRepeatableSection,
    visibleByConditionalRules,
    responseView,
    page,
    getAnswer,
    isInternalForm,
    isQuestionDirty,
    isQuestionReadonly,
    isQuestionValid,
    mode,
    onAnswerChanged,
    getQuestionError,
    questionIdToScroll,
    response,
    sessions,
    setQuestionIdToScroll,
    setResponse,
    willShowNotAllowedCursor,
  ])

  return (
    <div className={cn('px-4', className)}>
      {(hasText(section?.title) || hasText(section?.text)) && (
        <div>
          {hasText(section?.title) && (
            <div className="pt-3 text-lg font-semibold text-gray-700">
              {section.title}
            </div>
          )}
          {section.text && (
            <div className="text-md text-gray-400">{section.text}</div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
        {isFilledArray(section.questions)
          ? renderDeprecatedRows()
          : renderRows()}
      </div>
    </div>
  )
}

export default FormSection
